import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Session} from 'nutshell-core/session';
import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {ErrorState} from 'shells/error-state/error-state';
// @ts-expect-error - imported from .js/.jsx file
import {AudienceIcon} from 'shells/icon/svg/audience-icon';
import {ChatEllipsisSolidIcon} from 'shells/icon/svg/chat-ellipsis-solid-icon';
// @ts-expect-error - imported from .js/.jsx file
import {OutreachIcon} from 'shells/icon/svg/outreach-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ReportsIcon} from 'shells/icon/svg/reports-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';

import type {
    AudienceStub as AudienceStubFragment,
    Audience as AudienceFragment,
    AudienceUpdateInput,
} from 'nutshell-graphql-types';

import {EntityListTitleBarButtons} from '../../base/title-bar/entity-list-title-bar-buttons';
import {PageTitleBar} from '../../layout';
import {PageContainer} from '../../layout/page-container';
import {AppMessagesProvider} from '../../messages';
import type {ReportURLParams} from '../../reports/report-types';

import {
    exportSelectedContacts,
    sendBulkEmail,
    getBulkEditFilterParams,
} from '../entity-lists/helpers';
import * as listSelectionActions from '../entity-lists/list-selection-redux/list-selection-actions';
import * as listSelectionSelectors from '../entity-lists/list-selection-redux/list-selection-selectors';
import {AudienceMutations} from '../graphql-wrappers/audience/audience-mutations';
import {AudienceQuery} from '../graphql-wrappers/audience/audience-query';
import {MarketingStatsBanner} from '../marketing-stats-banner/marketing-stats-banner';

import {AddToAudienceModal} from './add-to-audience-modal/add-to-audience-modal';
import {AudienceMemberList} from './audience-list/audience-member-list';
import {AudienceOutreach} from './audience-outreach/audience-outreach';
import {AudienceOutreachSms} from './audience-outreach/audience-outreach-sms';
import {AudiencePageInfo} from './audience-page-info';
import {AudiencePageInfoNew} from './audience-page-info-new';
import * as audienceSelectors from './audiences-selectors';
import {getSelectedFields, getStatusInfo} from './helpers';

// This is only used for the app message container below, but I figured I could
// save us an extra file here
import styles from './audience-page-info.module.css';

type OwnProps = ContextRouter & {
    id: string;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    audience: AudienceFragment | null | undefined;
    audienceStub: AudienceStubFragment | null | undefined;
    isLoading: boolean;
    error: string | null | undefined;
    onAudienceSave: (saveData: AudienceUpdateInput) => Promise<any>;
};

function AudiencePageComponent(props: Props) {
    const {activeFilters, urlParams} = props;

    const [isAddFlowShown, setIsAddFlowShown] = React.useState<boolean>(false);
    const dispatch = useDispatch();

    const count = useSelector(audienceSelectors.getAudienceMemberListCount);
    const selectedItems = useSelector(listSelectionSelectors.getAudienceListSelectedItems);
    const selectionIsExcluded = useSelector(
        listSelectionSelectors.getAudienceListSelectionIsExcluded
    );
    const selectedCount = selectionIsExcluded ? count - selectedItems.length : selectedItems.length;

    const query = React.useMemo(
        () => ({
            filter: {selected: activeFilters},
            search: {selected: urlParams.q || ''},
        }),
        [activeFilters, urlParams.q]
    );

    const audienceData = props.audience ? props.audience : props.audienceStub;
    const pageTitle = audienceData ? `Audience • ${audienceData.name}` : 'Audience';
    const activeTab: string = props.match.params.tab || 'engagement';

    const tabOptions = [
        {
            id: 'engagement',
            label: 'Engagement',
            icon: ReportsIcon,
            href: generatePath(props.match.path, {
                audienceId: props.match.params.audienceId,
                tab: 'engagement',
            }),
            component: NutClientConfig.hasSmsAudiencesFF ? (
                <AudiencePageInfoNew
                    onParamChange={props.onParamChange}
                    onParamPush={props.onParamPush}
                    urlParams={props.urlParams}
                    audience={props.audience}
                    isLoading={props.isLoading}
                />
            ) : (
                <AudiencePageInfo
                    onParamChange={props.onParamChange}
                    onParamPush={props.onParamPush}
                    urlParams={props.urlParams}
                    audience={props.audience}
                    isLoading={props.isLoading}
                />
            ),
        },
        {
            id: 'members',
            label: 'Members',
            icon: AudienceIcon,
            href: generatePath(props.match.path, {
                audienceId: props.match.params.audienceId,
                tab: 'members',
            }),
            component: (
                <AudienceMemberList
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    urlParams={props.urlParams}
                    audience={props.audience}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    showFooter={selectedCount > 0}
                />
            ),
        },
        {
            id: 'outreach',
            label: 'Email outreach',
            icon: OutreachIcon,
            href: generatePath(props.match.path, {
                audienceId: props.match.params.audienceId,
                tab: 'outreach',
            }),
            component: <AudienceOutreach audienceId={props.id} />,
        },
        {
            id: 'sms-outreach',
            label: 'SMS outreach',
            icon: ChatEllipsisSolidIcon,
            href: generatePath(props.match.path, {
                audienceId: props.match.params.audienceId,
                tab: 'sms-outreach',
            }),
            component: <AudienceOutreachSms audienceId={props.id} />,
        },
    ];

    return (
        <>
            <PageContainer
                title={pageTitle}
                // Some pages don't need padding
                noPadding={
                    activeTab === 'members' ||
                    activeTab === 'outreach' ||
                    activeTab === 'sms-outreach'
                }
                pageTitleBar={
                    <PageTitleBar
                        title={props.audience ? props.audience.name : 'Audience'}
                        subheaderPrimaryText='Something about audiences'
                        noMaxWidth={true}
                        buttons={
                            <div className='flex gap-8'>
                                {activeTab === 'members' && (
                                    <EntityListTitleBarButtons
                                        query={query}
                                        entityType='contacts'
                                        massSelect={
                                            selectionIsExcluded || selectedItems.length === 0
                                        }
                                        selectedCount={selectedCount}
                                        selectedItems={selectedItems}
                                        omittedItems={selectionIsExcluded ? selectedItems : []}
                                        onEmailSend={(template, options) => {
                                            sendBulkEmail(
                                                template,
                                                options,
                                                selectedItems,
                                                selectionIsExcluded,
                                                'contacts',
                                                query,
                                                () =>
                                                    dispatch(listSelectionActions.resetSelection())
                                            );
                                        }}
                                        exportSelectedItems={() => {
                                            exportSelectedContacts(
                                                getBulkEditFilterParams(
                                                    selectionIsExcluded,
                                                    selectedItems,
                                                    props.activeFilters,
                                                    props.urlParams.q || ''
                                                ),
                                                getSelectedFields(props.urlParams),
                                                () =>
                                                    dispatch(listSelectionActions.resetSelection()),
                                                // @ts-expect-error
                                                () => dispatch(Session.pollForQueuedExports())
                                            );
                                        }}
                                        shouldShowExportAndEmailButtons={true}
                                        hideShareButton={true}
                                    />
                                )}
                                <Button
                                    onClick={() => setIsAddFlowShown(true)}
                                    disabled={props.isLoading}
                                    size='big'
                                    variant='primary'
                                >
                                    Add members
                                </Button>
                            </div>
                        }
                        breadcrumbs={[
                            {
                                to: '/marketing/audiences',
                                text: 'Audiences',
                            },
                        ]}
                        editInPlaceProps={{
                            onChange: (newValue: string | null | undefined) =>
                                props.audience
                                    ? props.onAudienceSave({
                                          audienceId: props.audience.id,
                                          name: newValue ? newValue : '',
                                      })
                                    : undefined,
                            placeholder: 'Name this audience',
                        }}
                    />
                }
                controlBar={
                    <PageControlBar
                        // @ts-expect-error
                        statusInfo={props.audience ? getStatusInfo(props.audience) : undefined}
                        tabs={{
                            selectedTab: activeTab,
                            options: tabOptions,
                        }}
                    />
                }
            >
                <MarketingStatsBanner />
                {/* @ts-expect-error */}
                <TabbedContent selectedTab={activeTab} tabOptions={tabOptions} />
                {props.error ? <ErrorState errorMessage={props.error} /> : undefined}
            </PageContainer>
            <div className={styles['app-messages-container']}>
                <AppMessagesProvider type='BulkEdits' />
            </div>
            {isAddFlowShown && props.audience ? (
                <AddToAudienceModal
                    audience={props.audience}
                    isOpen={true}
                    onClose={() => setIsAddFlowShown(false)}
                />
            ) : undefined}
        </>
    );
}

export function AudiencePage(props: OwnProps) {
    return (
        <AudienceQuery audienceId={props.id}>
            {({audience, audienceStub, error, isLoading}) => (
                <AudienceMutations>
                    {({onUpdate}) => (
                        <AudiencePageComponent
                            {...props}
                            audience={audience}
                            audienceStub={audienceStub}
                            isLoading={isLoading}
                            error={error}
                            onAudienceSave={onUpdate}
                        />
                    )}
                </AudienceMutations>
            )}
        </AudienceQuery>
    );
}
